import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Box, Container, Typography, TextField, Button, Paper, Alert, CircularProgress } from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { authAPI } from '../services/api';

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      const response = await authAPI.forgotPassword({ email });
      
      if (response.success) {
        setSuccess(true);
      } else {
        setError(response.error || 'Failed to send reset link.');
      }
    } catch (err: any) {
      console.error('Forgot password error:', err);
      setError(err.response?.data?.error || 'Failed to send reset link. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ bgcolor: '#f5f5f5', minHeight: 'calc(100vh - 64px - 300px)', py: { xs: 4, sm: 6, md: 8 } }}>
      <Container maxWidth="sm">
        <Paper elevation={3} sx={{ p: { xs: 3, sm: 4, md: 5 }, borderRadius: 3 }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <EmailIcon sx={{ fontSize: 60, color: 'black', mb: 2 }} />
            <Typography variant="h4" gutterBottom fontWeight={700} sx={{ fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
              Forgot Password?
            </Typography>
            <Typography variant="body1" color="text.secondary">
              No worries! Enter your email and we'll send you reset instructions.
            </Typography>
          </Box>

          {success ? (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <Alert severity="success" sx={{ mb: 3, textAlign: 'left' }}>
                <Typography variant="body1" gutterBottom>
                  <strong>Check your email!</strong>
                </Typography>
                <Typography variant="body2">
                  We've sent password reset instructions to <strong>{email}</strong>
                </Typography>
              </Alert>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Didn't receive the email? Check your spam folder or try again.
              </Typography>

              <Button
                variant="outlined"
                fullWidth
                onClick={() => setSuccess(false)}
                sx={{ mb: 2 }}
              >
                Send Again
              </Button>

              <Button
                component={Link}
                to="/login"
                variant="text"
                startIcon={<ArrowBackIcon />}
                fullWidth
              >
                Back to Login
              </Button>
            </Box>
          ) : (
            <>
              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}

              <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <TextField
                  fullWidth
                  label="Email Address"
                  name="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  autoFocus
                  disabled={loading}
                />

                <Button 
                  type="submit" 
                  variant="contained" 
                  size="large" 
                  fullWidth
                  disabled={loading || !email}
                  sx={{ mt: 2, py: 1.5 }}
                >
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </Button>

                <Button
                  component={Link}
                  to="/login"
                  variant="text"
                  startIcon={<ArrowBackIcon />}
                  fullWidth
                  disabled={loading}
                >
                  Back to Login
                </Button>
              </Box>
            </>
          )}
        </Paper>

        <Typography variant="body2" align="center" sx={{ mt: 3, color: 'text.secondary' }}>
          Remember your password?{' '}
          <Link to="/login" style={{ color: 'black', fontWeight: 600, textDecoration: 'none' }}>
            Sign in
          </Link>
        </Typography>
      </Container>
    </Box>
  );
};

export default ForgotPassword;

